import React, {useEffect, useState} from 'react';
import Icon from '@/shared/components/Icon';
import BloodViabilityService, {type BloodSampleViability} from '../services/bloodViabilityService';
import {logError} from '@/shared/utils/logger';

/**
 * Countdown banner shown above the BioMatch carousel when the patient
 * has a viable whole-blood sample. Drives the "add to existing draw"
 * upsell — clicking Enroll inside the window skips the home kit and
 * adds the program's tests to the lab's existing sample.
 *
 * Renders nothing when there's no sample, the sample isn't whole_blood,
 * or it's already expired.
 */

function formatRemaining(seconds: number): string {
    if (seconds <= 0) return '0:00:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const BloodViabilityCountdown: React.FC = () => {
    const [sample, setSample] = useState<BloodSampleViability | null>(null);
    const [secondsRemaining, setSecondsRemaining] = useState(0);

    useEffect(() => {
        let cancelled = false;
        BloodViabilityService.getMine()
            .then((s) => {
                if (cancelled) return;
                setSample(s);
                if (s) setSecondsRemaining(s.seconds_remaining);
            })
            .catch((err) => logError('Failed to load blood viability', err));
        return () => {
            cancelled = true;
        };
    }, []);

    // Tick once per second so the timer reads live. Stops auto-magically
    // when the sample expires (component re-renders with seconds=0 and
    // the effect's predicate gates further ticks).
    useEffect(() => {
        if (!sample || secondsRemaining <= 0) return;
        const id = window.setInterval(() => {
            setSecondsRemaining((s) => Math.max(0, s - 1));
        }, 1000);
        return () => window.clearInterval(id);
    }, [sample, secondsRemaining]);

    if (!sample || sample.sample_type !== 'whole_blood' || secondsRemaining <= 0) {
        return null;
    }

    return (
        <div className="mx-6 rounded-lg border border-mld-primary/30 bg-mld-primary/5 px-4 py-3">
            <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-mld-primary/15">
                    <Icon name="clock" className="h-5 w-5 text-mld-primary" />
                </div>
                <div className="flex-1">
                    <p className="text-sm font-semibold text-mld-text-primary">
                        You have <span className="font-mono">{formatRemaining(secondsRemaining)}</span> to add
                        tests without a new blood draw
                    </p>
                    <p className="text-xs text-mld-text-secondary">
                        Your blood sample from your recent screening is still viable. Enroll in any program
                        below and we'll run those tests on the existing sample — no kit, no second draw.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default BloodViabilityCountdown;
